import { LocalDate } from "@js-joda/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateSalaryPayoutFileRequest,
  SalaryPayoutPreviewResponse,
} from "model/Salary";
import { Api } from "services/api/api.service";
import { getBackendErrorMessageFromUnknown } from "services/api/backendError";
import { useAlertStore } from "stores/alertStore/alertStore";
import { resolveSalaryPayoutCreateErrorMessage } from "./salaryPayoutPanel.logic";

export type SalaryPayoutPreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | { status: "loaded"; data: SalaryPayoutPreviewResponse };

interface UseSalaryPayoutPanelParams {
  readonly companyId?: number;
  readonly workMonth: LocalDate;
}

interface UseSalaryPayoutPanelReturn {
  readonly canCreatePayoutFile: boolean;
  readonly excludedWorkerCount: number;
  readonly isCreatingPayoutFile: boolean;
  readonly isOpen: boolean;
  readonly payoutDate: LocalDate | null;
  readonly payoutWorkerCount: number;
  readonly previewState: SalaryPayoutPreviewState;
  readonly totalNetPayoutAmount: number;
  readonly closePanel: () => void;
  readonly createPayoutFile: () => Promise<void>;
  readonly openPanel: () => void;
  readonly setPayoutDate: (date: LocalDate | null) => void;
}

const getDefaultPayoutDate = (workMonth: LocalDate): LocalDate => {
  return workMonth.plusMonths(1).withDayOfMonth(25);
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

const uniqueByWorkerId = <TWorker extends { workerId: number }>(
  workers: readonly TWorker[],
): TWorker[] => {
  const uniqueWorkers = new Map<number, TWorker>();
  workers.forEach((worker) => {
    if (uniqueWorkers.has(worker.workerId)) return;
    uniqueWorkers.set(worker.workerId, worker);
  });
  return Array.from(uniqueWorkers.values());
};

const normalizeSalaryPayoutPreview = (
  preview: SalaryPayoutPreviewResponse,
): SalaryPayoutPreviewResponse => {
  const payoutWorkers = uniqueByWorkerId(preview.payoutWorkers);
  const payoutWorkerIds = new Set(
    payoutWorkers.map((worker) => worker.workerId),
  );

  const noPayoutWorkers = uniqueByWorkerId(preview.noPayoutWorkers).filter(
    (worker) => !payoutWorkerIds.has(worker.workerId),
  );
  const noPayoutWorkerIds = new Set(
    noPayoutWorkers.map((worker) => worker.workerId),
  );

  const noPaymentMethodWorkers = uniqueByWorkerId(
    preview.noPaymentMethodWorkers,
  ).filter((worker) => {
    return (
      !payoutWorkerIds.has(worker.workerId) &&
      !noPayoutWorkerIds.has(worker.workerId)
    );
  });

  return {
    noPaymentMethodWorkers,
    noPayoutWorkers,
    payoutWorkers,
  };
};

export const useSalaryPayoutPanel = ({
  companyId,
  workMonth,
}: UseSalaryPayoutPanelParams): UseSalaryPayoutPanelReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [previewState, setPreviewState] = useState<SalaryPayoutPreviewState>({
    status: "idle",
  });
  const [payoutDate, setPayoutDate] = useState<LocalDate | null>(() =>
    getDefaultPayoutDate(workMonth),
  );
  const [isCreatingPayoutFile, setIsCreatingPayoutFile] = useState(false);
  const isCreatingPayoutFileRef = useRef(false);
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();

  useEffect(() => {
    if (!isOpen) return;

    setPayoutDate(getDefaultPayoutDate(workMonth));

    if (companyId === undefined) {
      setPreviewState({ status: "error" });
      return;
    }

    const currentCompanyId = companyId;
    let isMounted = true;

    const loadPreview = async (): Promise<void> => {
      setPreviewState({ status: "loading" });

      try {
        const response = await Api().company.salary.getSalaryPayoutPreview({
          companyId: currentCompanyId,
          year: workMonth.year(),
          month: workMonth.monthValue(),
        });

        if (!isMounted) return;

        setPreviewState({
          status: "loaded",
          data: normalizeSalaryPayoutPreview(response.data),
        });
      } catch {
        if (!isMounted) return;
        setPreviewState({ status: "error" });
      }
    };

    void loadPreview();

    return () => {
      isMounted = false;
    };
  }, [companyId, workMonth, isOpen]);

  const payoutWorkerCount = useMemo((): number => {
    if (previewState.status !== "loaded") return 0;
    return previewState.data.payoutWorkers.length;
  }, [previewState]);

  const excludedWorkerCount = useMemo((): number => {
    if (previewState.status !== "loaded") return 0;
    return (
      previewState.data.noPaymentMethodWorkers.length +
      previewState.data.noPayoutWorkers.length
    );
  }, [previewState]);

  const totalNetPayoutAmount = useMemo((): number => {
    if (previewState.status !== "loaded") return 0;
    return previewState.data.payoutWorkers.reduce((sum, worker) => {
      return sum + worker.payoutAmount;
    }, 0);
  }, [previewState]);

  const canCreatePayoutFile =
    companyId !== undefined &&
    previewState.status === "loaded" &&
    previewState.data.payoutWorkers.length > 0 &&
    payoutDate !== null &&
    !isCreatingPayoutFile;

  const closePanel = useCallback((): void => {
    if (isCreatingPayoutFileRef.current) return;
    setIsOpen(false);
  }, []);

  const openPanel = (): void => {
    setIsOpen(true);
  };

  const createPayoutFile = async (): Promise<void> => {
    if (
      isCreatingPayoutFileRef.current ||
      companyId === undefined ||
      payoutDate === null ||
      previewState.status !== "loaded"
    ) {
      return;
    }

    const workerIds = previewState.data.payoutWorkers.map((worker) => {
      return worker.workerId;
    });

    if (workerIds.length === 0) return;

    const request: CreateSalaryPayoutFileRequest = {
      payoutDate: payoutDate.toString(),
      workerIds,
    };
    const workYear = workMonth.year();
    const workMonthValue = workMonth.monthValue();
    const payoutMonth = workMonth.plusMonths(1);

    isCreatingPayoutFileRef.current = true;
    setIsCreatingPayoutFile(true);

    try {
      const response = await Api().company.salary.createSalaryPayoutFile({
        companyId,
        year: workYear,
        month: workMonthValue,
        request,
      });

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/xml" });

      downloadBlob(
        blob,
        `Betalfil_${payoutMonth.year()}-${payoutMonth.monthValue()}.xml`,
      );
      setIsOpen(false);
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "checkmark",
          title: t("Salary.PaymentFileCreatedTitle"),
          message: t("Salary.PaymentFileCreatedMessage"),
        },
      });
    } catch (error: unknown) {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: resolveSalaryPayoutCreateErrorMessage(
            getBackendErrorMessageFromUnknown(error),
            t,
          ),
        },
      });
    } finally {
      isCreatingPayoutFileRef.current = false;
      setIsCreatingPayoutFile(false);
    }
  };

  return {
    canCreatePayoutFile,
    closePanel,
    createPayoutFile,
    excludedWorkerCount,
    isCreatingPayoutFile,
    isOpen,
    openPanel,
    payoutDate,
    payoutWorkerCount,
    previewState,
    setPayoutDate,
    totalNetPayoutAmount,
  };
};
